import { useState, useEffect } from 'react';
import States from '../constants/States';
import Enums from '../constants/Enums';
import Logger from '../utility/Logger';
import CacheManager from '../utility/CacheManager';

import getPayment from '../services/Payment';

const {
  localStorageKeys,
  paymentDetailsEnum,
} = Enums;
const { paymentDetailsStates } = States;

const storeDetails = details => window.localStorage.setItem(
  localStorageKeys.CREDIT_CARD_DETAILS_CACHE,
  JSON.stringify(details),
);

const getDetails = () => JSON.parse(
  window.localStorage.getItem(localStorageKeys.CREDIT_CARD_DETAILS_CACHE),
);
const updateDetails = (key, data) => {
  const details = getDetails();
  details[key] = data;
  storeDetails(details);
};

const parseIntAttribute = attribute => parseInt(attribute, 10);

const checkAttributes = (attributes, inputCCDetails) => {
  const ccDetails = inputCCDetails;
  if (attributes.hwCCInputCount) {
    const hwCCInputCountInt = parseIntAttribute(attributes.hwCCInputCount.value);
    if (hwCCInputCountInt > ccDetails[paymentDetailsEnum.HW_COUNT]) {
      ccDetails[paymentDetailsEnum.HW_COUNT] = hwCCInputCountInt;
      ccDetails[paymentDetailsEnum.CURRENT_TYPE] = paymentDetailsEnum.HW_TYPE;
      storeDetails(ccDetails);
      return true;
    }
  }
  if (attributes.hsCCInputCount) {
    const hsCCInputCountInt = parseIntAttribute(attributes.hsCCInputCount.value);
    if (hsCCInputCountInt > ccDetails[paymentDetailsEnum.HS_COUNT]) {
      ccDetails[paymentDetailsEnum.HS_COUNT] = hsCCInputCountInt;
      ccDetails[paymentDetailsEnum.CURRENT_TYPE] = paymentDetailsEnum.HS_TYPE;
      storeDetails(ccDetails);
      return true;
    }
  }
  return false;
};

const checkContact = (agent, setCC, originalContactId) => {
  const ccDetails = getDetails();
  const contacts = agent.getContacts();
  if (contacts[0]) {
    const attributes = contacts[0].getAttributes();
    if (ccDetails[paymentDetailsEnum.CONTACT_ID] === null && attributes && attributes.contactId)  {
      ccDetails[paymentDetailsEnum.CONTACT_ID] = attributes.contactId.value;
    }
    // Added original contact id
    if (ccDetails[paymentDetailsEnum.ORIGINAL_CONTACT_ID] === null) {
      ccDetails[paymentDetailsEnum.ORIGINAL_CONTACT_ID] = originalContactId
    }
    const connections = contacts[0].getConnections();
    if (connections[0]) {
      const isOnHold = connections[0].isOnHold();
      if (
        isOnHold
        && !ccDetails[paymentDetailsEnum.IS_HELD]
        && !ccDetails[paymentDetailsEnum.IS_DROPPED]
        && contacts[0].toSnapshot().contactData.connections.length === 3
        && contacts[0].toSnapshot().contactData.connections[2].state.type === 'connected'
      ) {
        setCC(paymentDetailsStates.HELD);
        ccDetails[paymentDetailsEnum.IS_HELD] = true;
        ccDetails[paymentDetailsEnum.IS_DISPLAYED] = false;        
      } else if (
        isOnHold
        && contacts[0].toSnapshot().contactData.connections[1].state.type === 'disconnected'
        && !ccDetails[paymentDetailsEnum.IS_DROPPED]
      ) {
        try {
          setCC(paymentDetailsStates.DROP);
          ccDetails[paymentDetailsEnum.IS_DROPPED] = true;
          ccDetails[paymentDetailsEnum.IS_HELD] = false;
          contacts[0].getAgentConnection().destroy({
            success() {
              Logger.info('success destroying contact after caller dropped');
            },
            failure(err) {
              Logger.error(`error attempting to destroy call ${JSON.stringify(err)}`);
            },
          });
        } catch (error) {
          Logger.error(error);
        }
      } else if (
        !isOnHold
        && ccDetails[paymentDetailsEnum.IS_HELD]
      ) {
        ccDetails[paymentDetailsEnum.IS_HELD] = false;
        const result = checkAttributes(attributes, ccDetails);
        if (result) {
          ccDetails[paymentDetailsEnum.IS_LOOKING] = true;
          storeDetails(ccDetails);
          return true;
        }
        setCC(paymentDetailsStates.EXIT);
      }
      storeDetails(ccDetails);
    }
  }
  return false;
};

const getPaymentDetails = (contactId, setCC) => {
  setCC(paymentDetailsStates.WAIT);
  updateDetails(paymentDetailsEnum.IS_WAITING, true);
  updateDetails(paymentDetailsEnum.IS_LOOKING, false);
  getPayment(contactId).then((res) => {
    updateDetails(paymentDetailsEnum.IS_WAITING, false);
    updateDetails(paymentDetailsEnum.IS_DISPLAYED, true);
    setCC(paymentDetailsStates.DISPLAY);
    updateDetails(paymentDetailsEnum.DETAILS_LIST, JSON.stringify(res.data.listOfCC));
  }).catch((err) => {
    Logger.error(err);
    setCC(paymentDetailsStates.ERROR);
    updateDetails(paymentDetailsEnum.IS_WAITING, false);
  });
};

const useCreditCardDetails = (contactData) => {
  const [creditCardDetails, setCreditCardDetails] = useState(paymentDetailsStates.EMPTY);
  useEffect(() => {
    Logger.info(JSON.stringify(creditCardDetails));
  }, [creditCardDetails]);

  const agents = window.connect.agent((agent) => {
    if (contactData.currentContact) {
      let originalContactId = contactData.currentContact.getOriginalContactId(); 
      console.log("originalContactId on useCreditCardDetails", originalContactId);
      agent.onRefresh(() => {
        try {
          const triggerAPI = checkContact(agent, setCreditCardDetails, originalContactId);
          if (triggerAPI) {
            const details = getDetails();
            getPaymentDetails(details[paymentDetailsEnum.CONTACT_ID], setCreditCardDetails);
            // getPaymentDetails(details[paymentDetailsEnum.ORIGINAL_CONTACT_ID], setCreditCardDetails);
          }
          return () => {};
        } catch (err) {
          Logger.error(err);
          return agents.unsubscribe();
        }
      });
      agent.onStateChange((agentStateChange) => {
        Logger.info(JSON.stringify(agentStateChange));
        if (
          agentStateChange.oldState === States.agentStateTypes.AFTER_CALL_WORK
        ) {
          setCreditCardDetails(paymentDetailsStates.EMPTY);
          CacheManager();
          return agents.unsubscribe();
        }
        return () => {};
      });
    }
    return () => { agents.unsubscribe(); };
  });
  const fetchPaymentDetails = () => {
    const details = getDetails();
    getPaymentDetails(details[paymentDetailsEnum.CONTACT_ID], setCreditCardDetails);
        // getPaymentDetails("1234", setCreditCardDetails);

  };
  return { creditCardDetails, setCreditCardDetails, fetchPaymentDetails };
};

export default useCreditCardDetails;
